import type { SxProps, Theme } from '@mui/material'
import { ClickAwayListener, InputAdornment, TextField as MuiTextField, Tooltip } from '@mui/material'
import React, { ReactElement } from 'react'
import { sizes } from 'styles/variables'
import styles from './NewTextField.module.less'

type NewTextFieldProps = {
  type?: 'text' | 'number'
  value?: string | number
  label: string
  unitLabel?: string
  errorMessage?: string
  maxWidth?: string
  fullWidth?: boolean
  helperText?: string
  disabled?: boolean
  required?: boolean
  autoFocus?: boolean
  onChange?: (value: string | number, e?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  onEnter?: (value?: string) => void
  onEscape?: (value?: string) => void
  onClickAway?: (value?: string) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void
  'data-testid'?: string
  inputProps?: Record<string, unknown>
  sx?: SxProps<Theme>
}

export default function NewTextField({
  type = 'text',
  value = '',
  label,
  unitLabel = '',
  errorMessage = '',
  helperText = '',
  disabled = false,
  required = false,
  autoFocus = false,
  maxWidth = '',
  fullWidth = true,
  onChange,
  onEnter,
  onEscape,
  onClickAway,
  onBlur,
  'data-testid': dataTestId,
  inputProps,
  sx,
  ...props
}: NewTextFieldProps): ReactElement {
  const infoText = errorMessage ? errorMessage : helperText

  if (unitLabel) {
    if (!inputProps) {
      inputProps = {}
    }

    inputProps.endAdornment = <InputAdornment position="end">{unitLabel}</InputAdornment>
  }

  return (
    <ClickAwayListener onClickAway={() => onClickAway?.()}>
      <span className={`${styles.NewTextField_TextFieldWrapper} ${styles.NewTextField_FixedHeightWrapper}`}>
        <MuiTextField
          fullWidth={fullWidth}
          style={{ maxWidth: maxWidth }}
          autoFocus={autoFocus}
          error={errorMessage ? true : false}
          aria-label={label}
          label={label}
          variant={'outlined'}
          value={value}
          type={type}
          helperText={
            <Tooltip title={infoText || ''} arrow placement="bottom-start">
              <span>{infoText}</span>
            </Tooltip>
          }
          onChange={(e) => onChange?.(e.target.value, e)}
          onKeyUp={(e) => {
            const value: string = (e.target as HTMLInputElement | HTMLTextAreaElement)?.value ?? ''

            if (onEnter && e?.key === 'Enter') {
              onEnter(value)
            } else if (onEscape && e?.key === 'Escape') {
              onEscape(value)
            }
          }}
          onBlur={(e) => {
            if (onBlur) {
              onBlur(e)
            }
          }}
          disabled={disabled}
          required={required}
          {...props}
          sx={[
            {
              '& .MuiOutlinedInput-input': {
                padding: '0px 14px',
                height: sizes.mainHeight,
              },
              '& .MuiInputLabel-root': {
                transform: 'translate(12px, 9px) scale(1)',
              },
              '& .MuiInputLabel-shrink': {
                transform: 'translate(12px, -6px) scale(0.75)',
              },
              '& .MuiFormHelperText-root': {
                maxWidth: '100%',
                boxSizing: 'border-box',
                marginBottom: '5px',
                marginLeft: '0px',
                fontSize: '10px',
              },
            },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          ]}
          slotProps={{
            input: inputProps,
            htmlInput: { 'data-testid': dataTestId },

            formHelperText: {
              sx: {
                whiteSpace: 'nowrap',
                '& > span': {
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              },
            },
          }}
        />
      </span>
    </ClickAwayListener>
  )
}
